import useFormatterPrice from '@/src/utils/formatter-price';
import getOption from '@/src/utils/getOption';
import useInterestRate from '@/src/utils/useInterestRate';
import FieldsRepeater from '@components/Form/FieldsRepeater';
import Modal from '@components/Modal';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSendMailMutation } from '@api/api';
import ModalTitle from '@components/ModalTitle';
import useATSName from '@/src/utils/useATSName';
import { MdArrowForwardIos } from 'react-icons/md';
import { emailWrapper } from '@/src/utils/emailWrapper';

export default function FinancialCalculator({
  price,
  refId,
}: {
  price: number | false;
  refId: string;
}) {
  const t = useTranslations('FinancingCalculator');
  const g = useTranslations('general');
  const ats = useATSName();
  const formatterPrice = useFormatterPrice();
  const [sendRequest, { isLoading, isSuccess, isError, error }] =
    useSendMailMutation();
  const [active, setActive] = useState(false);

  const handleToggle = () => {
    setActive(!active);
  };
  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const [result, setResult] = useState({
    grossPerYear: 0,
    interestPerYear: 0,
    totalPerYear: 0,
    totalPerMonth: 0,
  });
  const [financingFormOpened, setFinancingForm] = useState(false);
  const interest = useInterestRate();
  const buttonClasses =
    'grow rounded-sm bg-dark p-2 font-semibold uppercase text-white transition-all hover:bg-positive';

  const onSubmit = (data: any) => {
    const years = data.duration;
    const gross = parseFloat(data.price || 0) - parseFloat(data.deposit || 0);
    const shippingCost = data.includeShipping ? 10 : 0;
    const grossPerYear = gross / years;
    const interestPerYear = grossPerYear * interest;
    const totalPerYear = grossPerYear + interestPerYear;
    const totalPerMonth = totalPerYear / 12;

    setResult({
      grossPerYear,
      interestPerYear,
      totalPerYear,
      totalPerMonth,
    });
  };

  const onSubmitFinancing = (formData: any) => {
    let message = `<h1 style="color:#2D8477">${t('emailSubject')}</h1>`;
    const priorityFields = [
      'refId',
      'name',
      'email',
      'phone',
      'organizationNumber',
      'financingType',
      'price',
    ];
    priorityFields.forEach((key: keyof typeof formData) => {
      const label = t(key).includes('.') && key !== 'refId' ? g(key) : t(key);
      message = `${message}<br><b>${label}:</b><br> ${formData[key as keyof typeof formData]}<br>`;
    });
    Object.keys(formData).forEach((key: keyof typeof formData) => {
      if (priorityFields.includes(String(key))) {
        return;
      }
      if (['privacy'].includes(String(key))) {
        return;
      }
      if (!formData[key]) {
        return;
      }
      let label = t(String(key));
      if (label.includes('.')) {
        if (label.includes('-')) {
          label = label
            .split('-')
            .map((word) => t(word))
            .join(' / ');
        } else {
          label = String(key);
        }
      }
      message = `${message}<br><b>${label}:</b><br> ${formData[key]}<br>`;
    });

    sendRequest({
      reply: formData.email,
      subject: t('emailSubject'),
      content: emailWrapper(message),
    }).then(() => {
      setFinancingForm(false);
    });
  };

  const financingForm = (
    <Modal customClose={() => setFinancingForm(false)}>
      <div className='flex flex-col gap-2 text-white'>
        <ModalTitle title={t('modalTitle')} />
        {t('modalIntro', { ats })}
        <form onSubmit={handleSubmit(onSubmitFinancing)}>
          <div className='grid grid-cols-2 gap-6'>
            <FieldsRepeater
              errors={errors}
              register={register}
              t={t}
              setValue={setValue}
              trigger={trigger}
              fields={[
                {
                  name: 'name',
                  label: g('name'),
                  required: true,
                },
                {
                  name: 'phone',
                  label: g('phone'),
                  required: true,
                },
                {
                  name: 'email',
                  label: g('email'),
                  type: 'email',
                  required: true,
                },
                {
                  name: 'organizationNumber',
                  label: t('organizationNumber'),
                },
                {
                  name: 'financingType',
                  values: [t('leasing'), t('loan')],
                  required: true,
                },
              ]}
            />
          </div>
          <div className='mt-4 grid grid-cols-2 gap-4'>
            {[
              { label: t('refId'), value: refId },
              {
                label: t('price'),
                value: formatterPrice(
                  getValues('price') || 0,
                  String(getOption('DEFAULT_LOCALE'))
                ),
              },
              {
                label: t('deposit'),
                value: formatterPrice(
                  getValues('deposit') || 0,
                  String(getOption('DEFAULT_LOCALE'))
                ),
              },
              {
                label: t('duration'),
                value: `${getValues('duration')} ${t('years')}`,
              },
              {
                label: t('perMonth'),
                value: formatterPrice(
                  result.totalPerMonth,
                  String(getOption('DEFAULT_LOCALE'))
                ),
              },
            ].map((el) => {
              return (
                <div key={el.label}>
                  <div className='font-bold'>{el.label}</div>
                  <div>{el.value}</div>
                </div>
              );
            })}
          </div>
          <button
            type='submit'
            className='mt-6 rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
          >
            {g('submit')}
          </button>
          <p>{isLoading && <>{g('loading')}...</>}</p>
        </form>
      </div>
    </Modal>
  );

  return (
    <div className='rounded bg-brand-dark p-4 text-black'>
      <button
        onClick={() => handleToggle()}
        aria-expanded={active}
        className={`accordion-trigger block w-full pr-10 text-left lg:cursor-default`}
      >
        <h3 className='font-bold'>{t('title')}</h3>
        <span className='accordion-state lg:!hidden'>
          <MdArrowForwardIos />
        </span>
      </button>
      <div className='accordion-content lg:!grid-rows-1' aria-hidden={!active}>
        <div>
          {!getOption('FINANCIAL_CALCULATOR') ? (
            <p className='pt-2'>{t('calculatorAlternative')}</p>
          ) : (
            <form
              className='flex flex-col gap-2 pt-2'
              onSubmit={handleSubmit(onSubmit)}
            >
              <label className='flex flex-col'>
                {t('price')}:
                <input
                  type='number'
                  className='p-2'
                  {...(price && { defaultValue: price })}
                  {...register('price')}
                />
              </label>
              {/*<label>
              <input
                id='includeShipping'
                type='checkbox'
                {...register('includeShipping')}
              />{' '}
              Include Shipping
            </label>*/}
              <label className='flex flex-col'>
                {t('deposit')}:
                <input type='number' className='p-2' {...register('deposit')} />
              </label>
              <div className='flex flex-wrap gap-x-2'>
                <span className='w-full'>{t('duration')}:</span>
                <label>
                  <input
                    type='radio'
                    value={3}
                    {...register('duration')}
                    defaultChecked={true}
                  />{' '}
                  3 {t('years')}
                </label>
                <label>
                  <input type='radio' value={5} {...register('duration')} /> 5{' '}
                  {t('years')}
                </label>
                <label>
                  <input type='radio' value={7} {...register('duration')} /> 7{' '}
                  {t('years')}
                </label>
              </div>
              <input
                type='hidden'
                {...register('installmentCharges', {
                  value: process.env.NEXT_PUBLIC_INSTALLMENT_CHARGES,
                })}
              />
              <input
                type='hidden'
                {...register('refId', {
                  value: refId,
                })}
              />
              <button
                type='submit'
                disabled={!interest}
                className={buttonClasses}
              >
                {t('calculate')}
              </button>
            </form>
          )}

          {!!result.totalPerYear && (
            <div className='flex flex-col items-center gap-1'>
              <span className='text-2xl font-bold'>
                {formatterPrice(
                  result.totalPerMonth,
                  String(getOption('DEFAULT_LOCALE'))
                )}
              </span>
              {t('perMonth')}
              <button
                onClick={() => setFinancingForm(true)}
                className={buttonClasses}
              >
                {t('modalTitle')}
              </button>
            </div>
          )}
        </div>
      </div>
      {financingFormOpened && financingForm}
    </div>
  );
}
